import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { UserPlus } from 'lucide-react';
import { api } from '@/lib/api';
import type { Course, User } from '@/types';

export function AdminCoursesPage() {
  const queryClient = useQueryClient();
  const [selectedCourseId, setSelectedCourseId] = useState<string | null>(null);
  const [showEnrollModal, setShowEnrollModal] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);

  const { data: coursesData } = useQuery({
    queryKey: ['courses'],
    queryFn: () => api.getCourses(),
  });

  const { data: usersData } = useQuery({
    queryKey: ['users'],
    queryFn: () => api.getUsers(),
  });

  const courses = ((coursesData as any)?.courses || []) as Course[];
  const users = ((usersData as any)?.users || []) as User[];

  const enrollMutation = useMutation({
    mutationFn: ({ userId, courseId }: { userId: string; courseId: string }) =>
      api.enrollUser(userId, courseId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['enrollments'] });
    },
  });

  const handleBulkEnroll = async () => {
    if (!selectedCourseId) return;

    for (const userId of selectedUsers) {
      try {
        await enrollMutation.mutateAsync({
          userId,
          courseId: selectedCourseId,
        });
      } catch (error) {
        console.error(`Failed to enroll user ${userId}:`, error);
      }
    }

    alert(`Enrolled ${selectedUsers.length} users successfully!`);
    setShowEnrollModal(false);
    setSelectedUsers([]);
    setSelectedCourseId(null);
  };

  const toggleUserSelection = (userId: string) => {
    setSelectedUsers((prev) =>
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]
    );
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Course Management</h1>
          <p className="text-gray-600 mt-1">Manage courses and enrollments</p>
        </div>
      </div>

      {/* Courses List */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {courses.map((course) => (
          <div
            key={course.id}
            className="bg-white rounded-lg border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow"
          >
            <div className="h-48 bg-gradient-to-br from-red-400 to-red-600"></div>
            <div className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <span
                  className={`px-2 py-1 text-xs rounded-full ${
                    course.status === 'published'
                      ? 'bg-green-100 text-green-700'
                      : course.status === 'review'
                      ? 'bg-yellow-100 text-yellow-700'
                      : 'bg-gray-100 text-gray-700'
                  }`}
                >
                  {course.status}
                </span>
              </div>
              <h3 className="font-semibold text-lg mb-1">{course.title}</h3>
              <p className="text-gray-600 text-sm line-clamp-2 mb-4">
                {course.description || 'No description'}
              </p>

              <button
                onClick={() => {
                  setSelectedCourseId(course.id);
                  setShowEnrollModal(true);
                }}
                className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                <UserPlus className="w-4 h-4" />
                Enroll Users
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Bulk Enrollment Modal */}
      {showEnrollModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col">
            <h2 className="text-2xl font-bold mb-4">Enroll Users in Course</h2>

            <div className="flex-1 overflow-y-auto mb-4">
              <div className="space-y-2">
                {users
                  .filter((u) => u.role === 'learner')
                  .map((user) => (
                    <label
                      key={user.id}
                      className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={selectedUsers.includes(user.id)}
                        onChange={() => toggleUserSelection(user.id)}
                        className="w-4 h-4 text-red-600"
                      />
                      <div className="flex-1">
                        <div className="font-medium">{user.name}</div>
                        <div className="text-sm text-gray-600">{user.email}</div>
                      </div>
                    </label>
                  ))}
              </div>
            </div>

            <div className="flex justify-end gap-3 border-t pt-4">
              <button
                onClick={() => {
                  setShowEnrollModal(false);
                  setSelectedUsers([]);
                  setSelectedCourseId(null);
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleBulkEnroll}
                disabled={selectedUsers.length === 0}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Enroll {selectedUsers.length} User{selectedUsers.length !== 1 ? 's' : ''}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
